'use client';

import type { DetectedLoginBehavior } from '@terra/shared-contracts-applications';
import { Box, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { NumberInput } from '../../components/number-input';
import { Select, createSelectListCollection } from '../../components/select';
import { BodyStrong, MetaDefault } from '../../components/typography';
import { SettingsCard } from '../settings/SettingsCard';
import { SettingsRow } from '../settings/SettingsRow';

export type ConcurrentSessionsOption =
  | 'ALLOWED'
  | 'ONE_PER_ACCOUNT'
  | 'UNKNOWN';
export type LogoutScopeOption = 'THIS_SESSION' | 'ALL_SESSIONS' | 'UNKNOWN';
export type TokenRotationOption = 'ROTATES' | 'STABLE' | 'UNKNOWN';

export type LoginBehaviorConfigurationValue = {
  concurrentSessions: ConcurrentSessionsOption;
  logoutScope: LogoutScopeOption;
  tokenRotation: TokenRotationOption;
  maxParallelLogins: number | null;
};

export type LoginBehaviorConfigurationChange =
  Partial<LoginBehaviorConfigurationValue>;

export type LoginBehaviorConfigurationStatus = 'idle' | 'saving' | 'error';

export type LoginBehaviorConfigurationProps = {
  value: LoginBehaviorConfigurationValue;
  onChange: (change: LoginBehaviorConfigurationChange) => void;
  parallelExecutionEnabled: boolean;
  maxParallelLoginsLimit: number;
  defaultMaxParallelLogins: number;
  detected?: DetectedLoginBehavior;
  disabled?: boolean;
  status?: LoginBehaviorConfigurationStatus;
};

type LoginBehaviorSelectItem<Option extends string> = {
  value: Option;
  label: string;
};

type LoginBehaviorSelectProps<Option extends string> = {
  items: LoginBehaviorSelectItem<Option>[];
  value: Option;
  onValueChange: (value: Option) => void;
  disabled: boolean;
};

const CONTROL_WIDTH = '240px';

const LoginBehaviorSelect = <Option extends string>({
  items,
  value,
  onValueChange,
  disabled,
}: LoginBehaviorSelectProps<Option>) => {
  const collection = useMemo(
    () => createSelectListCollection({ items }),
    [items],
  );

  return (
    <Box w={CONTROL_WIDTH}>
      <Select.Root
        positioning={{ sameWidth: true, strategy: 'fixed' }}
        collection={collection}
        value={[value]}
        onValueChange={(details) => {
          const next = items.find((item) => item.value === details.value[0]);

          if (next) {
            onValueChange(next.value);
          }
        }}
        disabled={disabled}
        size="sm"
        width="full"
      >
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText />
            <ArrowDown01Icon size={14} />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            <Select.ItemGroup>
              {items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick01Icon size={16} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.ItemGroup>
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </Box>
  );
};

const toDraft = ({
  maxParallelLogins,
  defaultMaxParallelLogins,
}: {
  maxParallelLogins: number | null;
  defaultMaxParallelLogins: number;
}): string => String(maxParallelLogins ?? defaultMaxParallelLogins);

const detectionText = ({
  detected,
  label,
  notDetectedYet,
  differsByAccount,
}: {
  detected: DetectedLoginBehavior[keyof DetectedLoginBehavior] | undefined;
  label: (value: string) => string;
  notDetectedYet: string;
  differsByAccount: string;
}): string | undefined => {
  if (!detected) {
    return undefined;
  }

  if (detected.state === 'mixed') {
    return differsByAccount;
  }

  if (detected.state === 'detected' && detected.value) {
    return label(detected.value);
  }

  return notDetectedYet;
};

export const LoginBehaviorConfiguration = ({
  value,
  onChange,
  parallelExecutionEnabled,
  maxParallelLoginsLimit,
  defaultMaxParallelLogins,
  detected,
  disabled = false,
  status = 'idle',
}: LoginBehaviorConfigurationProps) => {
  const t = useTranslations('designSystem.automation.loginBehavior');
  const detectAutomatically = t('detectAutomatically');
  const notDetectedYet = t('notDetectedYet');
  const differsByAccount = t('differsByAccount');
  const [maxParallelLoginsDraft, setMaxParallelLoginsDraft] = useState(() =>
    toDraft({
      maxParallelLogins: value.maxParallelLogins,
      defaultMaxParallelLogins,
    }),
  );

  useEffect(() => {
    setMaxParallelLoginsDraft(
      toDraft({
        maxParallelLogins: value.maxParallelLogins,
        defaultMaxParallelLogins,
      }),
    );
  }, [defaultMaxParallelLogins, value.maxParallelLogins]);

  useEffect(() => {
    if (status === 'error') {
      setMaxParallelLoginsDraft(
        toDraft({
          maxParallelLogins: value.maxParallelLogins,
          defaultMaxParallelLogins,
        }),
      );
    }
  }, [defaultMaxParallelLogins, status, value.maxParallelLogins]);

  const concurrentSessionsItems = useMemo(
    (): LoginBehaviorSelectItem<ConcurrentSessionsOption>[] => [
      { value: 'UNKNOWN', label: detectAutomatically },
      { value: 'ALLOWED', label: t('concurrentSessions.allowed') },
      {
        value: 'ONE_PER_ACCOUNT',
        label: t('concurrentSessions.onePerAccount'),
      },
    ],
    [detectAutomatically, t],
  );
  const logoutScopeItems = useMemo(
    (): LoginBehaviorSelectItem<LogoutScopeOption>[] => [
      { value: 'UNKNOWN', label: detectAutomatically },
      { value: 'THIS_SESSION', label: t('logoutScope.thisSession') },
      { value: 'ALL_SESSIONS', label: t('logoutScope.allSessions') },
    ],
    [detectAutomatically, t],
  );
  const tokenRotationItems = useMemo(
    (): LoginBehaviorSelectItem<TokenRotationOption>[] => [
      { value: 'UNKNOWN', label: detectAutomatically },
      { value: 'ROTATES', label: t('tokenRotation.rotates') },
      { value: 'STABLE', label: t('tokenRotation.stable') },
    ],
    [detectAutomatically, t],
  );

  const sharesOneLogin = value.concurrentSessions === 'ONE_PER_ACCOUNT';
  const maxParallelLoginsDisabled =
    disabled || !parallelExecutionEnabled || sharesOneLogin;

  const commitMaxParallelLogins = () => {
    if (status === 'saving') {
      return;
    }

    const parsed = Number.parseInt(maxParallelLoginsDraft, 10);
    const next =
      Number.isInteger(parsed) && parsed !== defaultMaxParallelLogins
        ? Math.min(Math.max(parsed, 1), maxParallelLoginsLimit)
        : null;

    if (next === value.maxParallelLogins) {
      setMaxParallelLoginsDraft(
        toDraft({
          maxParallelLogins: next,
          defaultMaxParallelLogins,
        }),
      );

      return;
    }

    onChange({ maxParallelLogins: next });
  };

  return (
    <SettingsCard>
      <VStack gap={0.5} alignItems="stretch">
        <BodyStrong>{t('sectionTitle')}</BodyStrong>
        <MetaDefault color="fg.neutral.subtle">
          {t('sectionDescription')}
        </MetaDefault>
      </VStack>
      <SettingsRow
        label={t('concurrentSessions.label')}
        tooltip={t('concurrentSessions.tooltip')}
        description={
          value.concurrentSessions === 'UNKNOWN'
            ? detectionText({
                detected: detected?.concurrentSessions,
                label: (choice) =>
                  choice === 'ALLOWED'
                    ? t('concurrentSessions.allowed')
                    : t('concurrentSessions.onePerAccount'),
                notDetectedYet,
                differsByAccount,
              })
            : undefined
        }
        control={
          <LoginBehaviorSelect
            items={concurrentSessionsItems}
            value={value.concurrentSessions}
            onValueChange={(concurrentSessions) =>
              onChange({ concurrentSessions })
            }
            disabled={disabled}
          />
        }
      />
      <SettingsRow
        label={t('logoutScope.label')}
        tooltip={t('logoutScope.tooltip')}
        description={
          value.logoutScope === 'UNKNOWN'
            ? detectionText({
                detected: detected?.logoutScope,
                label: (choice) =>
                  choice === 'THIS_SESSION'
                    ? t('logoutScope.thisSession')
                    : t('logoutScope.allSessions'),
                notDetectedYet,
                differsByAccount,
              })
            : undefined
        }
        control={
          <LoginBehaviorSelect
            items={logoutScopeItems}
            value={value.logoutScope}
            onValueChange={(logoutScope) => onChange({ logoutScope })}
            disabled={disabled}
          />
        }
      />
      <SettingsRow
        label={t('tokenRotation.label')}
        tooltip={t('tokenRotation.tooltip')}
        description={
          value.tokenRotation === 'UNKNOWN'
            ? detectionText({
                detected: detected?.tokenRotation,
                label: (choice) =>
                  choice === 'ROTATES'
                    ? t('tokenRotation.rotates')
                    : t('tokenRotation.stable'),
                notDetectedYet,
                differsByAccount,
              })
            : undefined
        }
        control={
          <LoginBehaviorSelect
            items={tokenRotationItems}
            value={value.tokenRotation}
            onValueChange={(tokenRotation) => onChange({ tokenRotation })}
            disabled={disabled}
          />
        }
      />
      <SettingsRow
        label={t('maxParallelLogins.label')}
        tooltip={t('maxParallelLogins.tooltip')}
        description={
          sharesOneLogin
            ? t('maxParallelLogins.sharedLoginHint')
            : parallelExecutionEnabled
              ? value.maxParallelLogins === null
                ? t('maxParallelLogins.defaultValue')
                : undefined
              : t('maxParallelLogins.parallelExecutionOffHint')
        }
        control={
          <Box w={CONTROL_WIDTH}>
            <NumberInput
              width="full"
              value={maxParallelLoginsDraft}
              min={1}
              max={maxParallelLoginsLimit}
              disabled={maxParallelLoginsDisabled}
              formatOptions={{ maximumFractionDigits: 0 }}
              onValueChange={(details) =>
                setMaxParallelLoginsDraft(details.value)
              }
              onBlur={commitMaxParallelLogins}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  commitMaxParallelLogins();
                }
              }}
            />
          </Box>
        }
      />
    </SettingsCard>
  );
};
